import { toast } from 'react-toastify'
import mapper from '../../../../_endpoints'
import { usePost } from '../../../../_hooks/usePost'

export type ArcRoleInfo = { role: string; group?: string; description?: string }

// Raw agents carry the full role catalog as a JSON string, an array of strings, or an array of
// {role/Role, group/Group, description/Description} objects (API casing varies) - cover all of them.
const parseArcRoles = (agent: any): any[] => {
  let arcRoles = agent?.arcRoles ?? agent?.metadata?.arcRoles
  if (typeof arcRoles === 'string') {
    try {
      arcRoles = JSON.parse(arcRoles)
    } catch {
      arcRoles = []
    }
  }
  return Array.isArray(arcRoles) ? arcRoles : []
}

// Builds the role list for display (role/group/description), keyed off arc.arcRoles (the
// authoritative list of grantable role names) and enriched with the fuller role catalog when present.
export const getArcRoleOptions = (agent: any): ArcRoleInfo[] => {
  const detailsByRole = new Map<string, ArcRoleInfo>()
  parseArcRoles(agent).forEach((entry: any) => {
    if (typeof entry === 'string') {
      if (!detailsByRole.has(entry)) detailsByRole.set(entry, { role: entry })
      return
    }
    const role = entry?.role ?? entry?.Role
    if (!role) return
    detailsByRole.set(role, {
      role,
      group: entry.group ?? entry.Group,
      description: entry.description ?? entry.Description
    })
  })

  const roleNames: string[] = agent?.arc?.arcRoles?.length
    ? agent.arc.arcRoles
    : Array.from(detailsByRole.keys())

  return roleNames.map((role) => detailsByRole.get(role) || { role })
}

// Shared by AgentCards and MyAgents so the ARC request payload/mutation only lives in one place.
export const useAgentArcService = () => {
  const [isRequestingArcAccess, postArcAccess] = usePost()

  const requestAgentArcAccess = async (agent: any, role?: string) => {
    // Raw Nexus agent objects (AgentData) carry the marketplace uniqueName nested under metadata,
    // with agent.id/metadata left as their own internal id - sending that as uniqueName 400s.
    // AgentDetail/MarketplaceAgent have no metadata and map id directly from uniqueName, so id is safe there.
    const uniqueName = agent.uniqueName ?? agent.metadata?.uniqueName ?? agent.id

    const arcRoleNames = role ? [role] : getArcRoleOptions(agent).map((r) => r.role)

    const response = await postArcAccess({
      url: mapper.marketplaceAgents.agentArcRequest,
      data: {
        title: agent.name,
        uniqueName: String(uniqueName),
        arcRoles: arcRoleNames
      }
    })
    if (response?.isSuccess) {
      toast.success('Agent access request raised successfully')
    } else {
      toast.error(response?.message || 'Failed to raise agent access request')
    }
    if (agent.arc) agent.arc.isInProcess = true
    return response
  }

  return { isRequestingArcAccess, requestAgentArcAccess, getArcRoleOptions }
}
